import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { GetWSMgr } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { useRouteState } from "./useRouteState";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumChartOpenModes, enumUIModes } from "@coreapp/constants";

// Module-scoped, NOT per-mount. The post-login initial-load skip is a
// session concept — FinishLogin dispatched the cold-start load exactly
// once. Route elements in Root.tsx are uniformly PermissionGate-wrapped
// to keep element identity stable across canvas routes, but this flag
// is still session-scoped as a defense against any future element-
// identity mismatch that would unmount IndexPage and reset useRefs.
let postLoginInitialLoadHandled = false;

/** Test-only: reset the module-scoped post-login flag between cases.
 *  Production uses the LoginComplete topic flow; do not call from app code. */
export const __resetPostLoginInitialLoadHandledForTests = () => {
  postLoginInitialLoadHandled = false;
};

/**
 * Loads the document identified by resource route params (/base/:chartId, /open/*, /plan/:planId).
 * Skips when wsMgr is absent, route is mode-only, or resource hasn't changed since last load.
 */
export const useDocumentLoader = () => {
  const { resourceType, chartId, planId, directory, file } = useRouteState();
  const [searchParams] = useSearchParams();
  const hasCreate = searchParams.has("create");
  const [loginComplete, setLoginComplete] = useState(() => !!GetWSMgr()?.fLoadComplete);
  useTopic("LoginComplete", () => setLoginComplete(true));
  useTopic("logout", () => {
    postLoginInitialLoadHandled = false;
    setLoginComplete(false);
  });
  const loadedResourceRef = useRef<string | null>(null);

  useEffect(() => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !loginComplete) {
      return;
    }

    // Passenger routes — no doc to load, don't touch loadedResourceRef.
    if (resourceType === "mode-only" || resourceType === "boxlayout") {
      // Cold-start load failure routes here (/chart); FinishLogin's initial
      // load is spent — consume the skip so the next resource nav loads.
      if (resourceType === "mode-only") postLoginInitialLoadHandled = true;
      return;
    }

    // Build a resource key to detect changes
    const resourceKey = `${resourceType}:${chartId ?? ""}:${planId ?? ""}:${directory ?? ""}:${file ?? ""}`;

    // ?create=true: coreapp owns doc state (create-from-base or PIT archive).
    // Reloading would CloseDocument mid-flow. allows cross base chart custom chart creation
    if (hasCreate) {
      loadedResourceRef.current = resourceKey;
      return;
    }

    if (resourceKey === loadedResourceRef.current) {
      if (!hasCreate && resourceType === "base" && chartId) {
        const doc = wsMgr.GetDoc();
        const loadedChartId = doc?.IsServerChart() ? String(doc.GetChartID()) : null;
        if (doc && loadedChartId !== chartId && !doc.IsCustomChart?.()) {
          wsMgr.CloseDocument(false);
          Globals._accountInfo.fLaunchChartID = chartId;
          Globals._uiConfig.SetChartOpenMode(enumChartOpenModes.masterchart);
          wsMgr.HandleDocChange(enumUIModes.chart);
        }
      }
      return;
    }

    // Skip the first post-login fire. FinishLogin → enterMode →
    // HandleDocChange owns the initial load; it reads the URL and
    // sets the correct doc config globals. Module-scoped (not
    // per-mount) so cross-route remounts don't re-trigger the skip
    // and swallow in-session resource switches.
    if (!postLoginInitialLoadHandled) {
      postLoginInitialLoadHandled = true;
      loadedResourceRef.current = resourceKey;
      return;
    }

    loadedResourceRef.current = resourceKey;

    switch (resourceType) {
      case "base":
        if (chartId) {
          const doc = wsMgr.GetDoc();
          const loadedChartId =
            doc?.IsServerChart?.() && !doc?.IsPlanChart?.() ? String(doc.GetChartID() ?? "") : null;
          if (loadedChartId === chartId) break;
          wsMgr.CloseDocument(false);
          Globals._accountInfo.fLaunchChartID = chartId;
          Globals._uiConfig.SetChartOpenMode(enumChartOpenModes.masterchart);
          wsMgr.HandleDocChange(enumUIModes.chart);
        }
        break;

      case "open":
        if (directory && file) {
          // document already loaded
          const docMgr = wsMgr.GetDocMgr?.();
          if (docMgr?.GetFileName?.(true) === file && docMgr?.GetFileDirectory?.() === directory) {
            break;
          }
          wsMgr.CloseDocument(false);
          Globals._uiConfig.SetDocConfig(directory, file);
          Globals._uiConfig.SetChartOpenMode(enumChartOpenModes.opendoc);
          wsMgr.HandleDocChange(enumUIModes.chart);
        }
        break;

      case "plan":
        if (planId) {
          const id = parseInt(planId, 10);
          // plan already loaded
          const planMgr = wsMgr.GetPlanMgr?.();
          if (planMgr?.GetPlanID?.() === id) break;
          const svrChartMgr = (Globals as any)._svrChartMgr;
          if (svrChartMgr) {
            svrChartMgr.SetLastPlanID(id);
          }
          wsMgr.OpenPlan(id);
        }
        break;
    }
  }, [resourceType, chartId, planId, directory, file, hasCreate, loginComplete]);
};
